/**
 * Check this team (AIOE 0.65, 26 Oct 2026): what is wrong with an organisation team, in words, and the fixes a
 * person may make with one button. AIOE's own contract: the console and the workbench read it (the workbench
 * mirrors this file byte for byte; only the import line may differ).
 *
 *   GET  /teams/v1/:teamId/check[?projectId=][&include=agent-identity]
 *                                                TeamCheck      whoever may read the team; 404 for anyone else
 *   POST /teams/v1/:teamId/repair                TeamRepairAnswer   { actions: TeamRepairAction[], projectId? }
 *   GET  /teams/v1/attention                     TeamAttention  the teams with errors or warnings the caller may read; ETag, 304
 *
 * The check reads only what the organisation stores (the team, its policy, its projects, its catalogue and the
 * fleet's last reports); it never asks a machine. A finding is an `error` when the team cannot work as defined
 * (a member will not start, a save will be refused), a `warning` when it works but not as meant, and `info` for
 * something worth knowing. `fix` is there only when the caller may make that fix; otherwise `words` say who can.
 *
 * `include` names checks a client reads that AIOE added after 0.67.0 (comma-separated; today only `agent-identity`).
 * AIOE emits such a finding only to a client that asks for it, so a client whose copy of this file predates a kind
 * never receives a check it cannot read. The attention list counts only the checks every client reads.
 *
 * A repair applies each action the caller may make and refuses the rest, in words; every action is audited
 * (`team.repair`, outcome `denied` for a refusal). The answer carries the check made afterwards.
 */
import { z } from "zod";
export const TEAM_CHECK_SCHEMA = 1;
export const TEAM_CHECK_SEVERITIES = ["error", "warning", "info"];
/**
 * Which check a finding comes from:
 *  - `leftover-fields`: a member holds an old runtime's or provider's fields (sign-in, key name, account, model);
 *  - `policy`: the organisation's policy does not allow a member's runtime, provider or model;
 *  - `project-grant`: the team is not granted to the project asked about, or is granted to a project that is gone;
 *  - `clashing-id`: a machine holds a different team under this id, or another organisation team looks like this one;
 *  - `old-version`: machines hold an older version of the team than the organisation's;
 *  - `host-machine`: a member is bound to a machine that is gone, refused, or long offline;
 *  - `definition`: the team names what the organisation does not hold (a catalogue role or skill, a specialist, its lead);
 *  - `delegation`: who answers for whom (teamDelegation.ts `delegationProblems`) names someone who is no longer one of
 *    the team's people, as an answerer or in a cover rule, goes round in a cover loop, or lists a topic or a covered
 *    person twice. A fix only ever takes someone out (never puts the owner or anyone else in their place);
 *  - `agent-identity` (after AIOE 0.67.0, only with `include=agent-identity`): the organisation still keeps Microsoft agent
 *    identities for members who left the team or were renamed. The fix brings the team's identities up to date
 *    (`sync-agent-identities`, administrators): a renamed member keeps its identity, a member who left loses its
 *    registry card, and its identity waits in Entra for the organisation's check for identities that belong to nobody.
 *
 * The workbench adds its own findings about one computer to the same list (AIOE never emits them; findings validate on
 * both sides, fixes are per side): `local-runtime` (the runtime is not installed or turned off there), `local-provider` (the provider
 * is not set up there), `local-model` (the model is not available there), `local-credential` (the key or account is not
 * on that computer) and `local-placement` (where the member is placed cannot run there).
 */
export const TEAM_CHECK_KINDS = ["leftover-fields", "policy", "project-grant", "clashing-id", "old-version", "host-machine", "definition", "delegation", "agent-identity", "local-runtime", "local-provider", "local-model", "local-credential", "local-placement"];
/** The member fields an old runtime or provider leaves behind. */
export const TEAM_PROVIDER_FIELDS = ["providerId", "credential", "account", "model"];
const memberName = z.string().min(1).max(100);
const projectId = z.string().min(1).max(200);
/** A person delegation names (a subject id; teamDelegation.ts). */
const personId = z.string().min(1).max(200);
/** One fix, as the check offers it and the repair takes it. */
export const teamRepairActionSchema = z.discriminatedUnion("kind", [
    /** Clear the fields a member's old runtime or provider left behind. */
    z.object({ kind: z.literal("clear-stale-provider"), member: memberName, fields: z.array(z.enum(TEAM_PROVIDER_FIELDS)).min(1).max(TEAM_PROVIDER_FIELDS.length) }),
    /** Clear a member's model the policy does not allow: the member then uses its runtime's default model. */
    z.object({ kind: z.literal("clear-model"), member: memberName }),
    /** Grant the team to a project (its owners, contributors and portfolio managers may). */
    z.object({ kind: z.literal("grant-to-project"), projectId }),
    /** Take a grant to a project that no longer exists off the team. */
    z.object({ kind: z.literal("drop-grant"), projectId }),
    /** Unbind a member from a machine that is gone, refused or long offline: it runs where the team runs. */
    z.object({ kind: z.literal("drop-host"), member: memberName }),
    /** Take the catalogue roles and skills the organisation no longer holds off a member. */
    z.object({ kind: z.literal("drop-missing-catalogue"), member: memberName }),
    /** Clear the lead when it names nobody in the team. */
    z.object({ kind: z.literal("clear-lead") }),
    /** Clear a member's link to a specialist the organisation no longer holds. */
    z.object({ kind: z.literal("clear-specialist"), member: memberName }),
    /** Ask these machines to load the organisation's version now (a device order; administrators). */
    z.object({ kind: z.literal("refresh-machines"), deviceIds: z.array(z.string().min(1).max(100)).min(1).max(100) }),
    /** Fold another organisation team that is the same team into this one (portfolio managers). */
    z.object({ kind: z.literal("merge-duplicate"), from: z.string().min(1).max(100) }),
    /** Take people who left the team off a delegation topic's answerers; offered only while someone else still answers it. */
    z.object({ kind: z.literal("drop-answerers"), topic: z.string().min(1).max(100), people: z.array(personId).min(1).max(20) }),
    /** Take people who left the team out of the cover rule for `person` (`by`), or, without `by`, remove that rule. */
    z.object({ kind: z.literal("drop-cover"), person: personId, by: z.array(personId).min(1).max(20).optional() }),
    /** Remove the delegation topics and cover rules that are exact copies of an earlier one. */
    z.object({ kind: z.literal("drop-delegation-copies") }),
    /** Bring the team's Microsoft agent identities up to date with its members now (administrators; after AIOE 0.67.0). */
    z.object({ kind: z.literal("sync-agent-identities") }),
]);
export const teamCheckFindingSchema = z.object({
    /** Stable for the same problem across checks (`leftover-fields:Coder`), so a client can tell a new finding from an old one. */
    id: z.string().min(1).max(300),
    check: z.enum(TEAM_CHECK_KINDS),
    severity: z.enum(TEAM_CHECK_SEVERITIES),
    /** The member it is about, by name. */
    member: memberName.optional(),
    /** What is wrong and what it means, in a sentence or two. */
    words: z.string().min(1).max(1000),
    /** The fix the caller may make, as one button. */
    fix: z.object({ action: teamRepairActionSchema, label: z.string().min(1).max(100) }).optional(),
});
/** The checks a client may ask for with `include` (they are emitted only when asked). */
export const TEAM_CHECK_OPT_IN_KINDS = ["agent-identity"];
export const teamCheckSchema = z.object({
    schema: z.literal(TEAM_CHECK_SCHEMA).optional(),
    teamId: z.string().min(1).max(100),
    /** The team's version the check read. */
    version: z.number().int().nonnegative().optional(),
    /** The project the check was asked about, when one was. */
    projectId: projectId.optional(),
    checkedAt: z.string(),
    findings: z.array(teamCheckFindingSchema).max(200),
});
export const teamRepairRequestSchema = z.object({
    actions: z.array(teamRepairActionSchema).min(1).max(50),
    /** The project the person is checking from: the re-check in the answer is asked about it. */
    projectId: projectId.optional(),
    /** The opt-in checks the re-check in the answer carries, as `include` on the check; a kind AIOE does not know is ignored. */
    include: z.array(z.string().min(1).max(50)).max(20).optional(),
});
/** One action's outcome, in words ("Cleared Coder's old sign-in: providerId, credential"). */
export const teamRepairOutcomeSchema = z.object({
    action: teamRepairActionSchema,
    words: z.string().min(1).max(1000),
});
/**
 * Why an action was refused: `not_allowed` (the caller may not make it), `not_needed` (nothing to change: it was
 * fixed already), `not_found` (no such member, project, machine or team), `failed` (the platform could not).
 */
export const TEAM_REPAIR_REFUSALS = ["not_allowed", "not_needed", "not_found", "failed"];
export const teamRepairAnswerSchema = z.object({
    teamId: z.string().min(1).max(100),
    /** The team's version after the repair. */
    version: z.number().int().nonnegative(),
    applied: z.array(teamRepairOutcomeSchema),
    refused: z.array(teamRepairOutcomeSchema.extend({ reason: z.enum(TEAM_REPAIR_REFUSALS) })),
    /** The check made after the repair, about the same project. */
    check: teamCheckSchema,
});
/** A team that needs attention: how many errors and warnings its check finds now (info is not counted). */
export const teamAttentionRowSchema = z.object({
    teamId: z.string().min(1).max(100),
    /** Its name and kind, so a list of them reads without another call. */
    name: z.string().min(1).max(200),
    kind: z.enum(["team", "specialist"]),
    errors: z.number().int().nonnegative(),
    warnings: z.number().int().nonnegative(),
});
/** `GET /teams/v1/attention`: only the teams with an error or a warning, that the caller may read. */
export const teamAttentionSchema = z.object({
    teams: z.array(teamAttentionRowSchema),
    checkedAt: z.string(),
});
/** The plain words for a repair action's refusal reason, for a client that has only the code. */
export const TEAM_REPAIR_REFUSAL_WORDS = {
    not_allowed: "You may not make this fix.",
    not_needed: "Nothing to fix: it is already right.",
    not_found: "It is no longer there.",
    failed: "The platform could not make this fix.",
};
//# sourceMappingURL=teamCheck.js.map